import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7hvb2vhkiob2r09iqcg';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.message.trim()) newErrors.message = 'Le message est requis';
    if (formData.message.length > 1000) newErrors.message = 'Le message ne peut pas dépasser 1000 caractères';
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setStatus('loading');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ nom: '', prenom: '', email: '', telephone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center">
            <span className="inline-block bg-teal-50 text-teal-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Support
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Contactez-nous</h1>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Une question, une suggestion ou besoin d&apos;aide ? Notre équipe est là pour vous accompagner.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Info cards */}
          <div className="space-y-4">
            {[
              { icon: 'ri-mail-line', title: 'Email', value: 'contact@dt1symply.lbtdesign.fr', color: 'bg-teal-50 text-teal-600' },
              { icon: 'ri-phone-line', title: 'Téléphone', value: '+33 1 23 45 67 89', color: 'bg-emerald-50 text-emerald-600' },
              { icon: 'ri-time-line', title: 'Disponibilité', value: 'Lun-Ven, 9h-18h', color: 'bg-sky-50 text-sky-600' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.color} shrink-0`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">{item.title}</div>
                  <div className="text-gray-800 font-semibold text-sm">{item.value}</div>
                </div>
              </div>
            ))}

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-stethoscope-line text-amber-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-amber-800 text-sm mb-1">Avertissement médical</div>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Ce site ne remplace pas l&apos;avis d&apos;un professionnel de santé. Consultez votre médecin pour tout ajustement de traitement.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                  <i className="ri-check-line text-emerald-600 text-3xl"></i>
                </div>
                <h3 className="font-bold text-gray-900 text-xl mb-2">Message envoyé !</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-dt1"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Nom */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={(e) => handleChange('nom', e.target.value)}
                      placeholder="Dupont"
                      className={`w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors ${errors.nom ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-teal-400'}`}
                    />
                    {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
                  </div>
                  {/* Prénom */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={(e) => handleChange('prenom', e.target.value)}
                      placeholder="Marie"
                      className={`w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors ${errors.prenom ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-teal-400'}`}
                    />
                    {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="marie.dupont@email.fr"
                    className={`w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors ${errors.email ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-teal-400'}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={(e) => handleChange('telephone', e.target.value)}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full border border-gray-200 focus:border-teal-400 rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 1000) handleChange('message', e.target.value);
                    }}
                    placeholder="Décrivez votre question ou suggestion..."
                    rows={5}
                    className={`w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors resize-none ${errors.message ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-teal-400'}`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {errors.message ? (
                      <p className="text-red-500 text-xs">{errors.message}</p>
                    ) : <span />}
                    <span className={`text-xs ${formData.message.length > 900 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {formData.message.length}/1000
                    </span>
                  </div>
                </div>

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
                    Une erreur est survenue. Veuillez réessayer.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
